import { useEffect, useState } from 'react';
import { base64ToBlob } from '../../util';

/**
 * Get a usable object url ("handle to blob") from base64-encoded data.
 *
 * This can be used to display the data e.g. in a pdfjs iframe.
 * The ObjectURL is correctly revoked when the calling component is unmounted or the base64 data or mimetype change.
 *
 * @author Bennett Piater
 *
 * @param data input data (base64 or Blob)
 * @param mimetype of the binary data, set on the Blob
 */
export function useObjectURL(
  data: string | Blob,
  mimetype: string
): string | undefined {
  const [objectUrl, setObjectUrl] = useState<string | undefined>(undefined);

  useEffect(() => {
    const blob = data instanceof Blob ? data : base64ToBlob(data, mimetype);
    const url = window.URL.createObjectURL(blob);
    setObjectUrl(url);

    // Return cleanup callback ('destructor').
    // If you don't understand this yet, you need to go read the hook documentation front-to-back right now :-*
    return () => {
      setObjectUrl(undefined);
      window.URL.revokeObjectURL(url);
    };
  }, [data, mimetype, setObjectUrl]);

  return objectUrl;
}
